/**
 * Builds the shareable identifiers/URLs for a wiki article or publication
 * (index or section) — used by `ShareDialog`. Centralized here so the
 * "how do I refer to this event from outside Wikistar" logic isn't
 * duplicated between the dialog and any future share entry points.
 */
import { nip19 } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';
import { getTagValue } from '@/lib/nip54';

/**
 * The canonical Wikistar app URL for `event` — a `naddr1...`-based path
 * under the current origin, so the link works from any browser regardless
 * of whether the visitor has a Nostr client installed. Returns `undefined`
 * if `event` has no `d` tag (shouldn't happen for valid wiki/publication
 * events, but the type doesn't guarantee it).
 */
export function shareableAppUrl(event: NostrEvent): string | undefined {
  const d = getTagValue(event, 'd');
  if (!d) return undefined;
  const naddr = nip19.naddrEncode({ kind: event.kind, pubkey: event.pubkey, identifier: d });
  return `${window.location.origin}/${naddr}`;
}

/** A `nostr:naddr1...` URI (NIP-21) for opening the event directly in any Nostr client. */
export function shareableNostrUri(event: NostrEvent): string | undefined {
  const d = getTagValue(event, 'd');
  if (!d) return undefined;
  const naddr = nip19.naddrEncode({ kind: event.kind, pubkey: event.pubkey, identifier: d });
  return `nostr:${naddr}`;
}

export function socialShareUrls(title: string, url: string) {
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(title);
  return {
    x: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    reddit: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedText}`,
    email: `mailto:?subject=${encodedText}&body=${encodedUrl}`,
  };
}
